import React from 'react';

import cs from 'classnames';
import { get } from 'lodash';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useGetPermissions } from 'lib/hooks/queries/permissions/usePermissions';

import { currentPractice } from 'components/practice/practiceState';

import state from '../../state';
import Icon from '../Icon/Icon';
import { withErrorBoundary } from '../Error/Boundary';
import { hasModule as checkModule, pm } from '../../../lib/helpers/utility';

const PermissionsAllowed = ({
  requiredPermissions = '',
  requiredModules = '',
  displaySettings,
  isAny = false,
  isAnyModule = false,
  showMessage = false,
  showIllustration = false,
  customMessage = 'You do not have the necessary permissions',
  description = 'You are not authorized to access this resource.',
  messageClassName = '',
  className = '',
  children
}) => {
  const { display_settings } = useRecoilValue(currentPractice) || {};
  const [permissions, setPermissions] = useRecoilState(state.permissions);
  const practiceModules = useRecoilValue(state.modulesState);
  useGetPermissions({
    options: {
      enabled: !permissions,
      onSuccess: (data) => {
        const newPermissions = data?.permissions;
        setPermissions({ ...newPermissions, loaded: true });
      }
    }
  });

  const renderMessage = () => {
    return (
      <div
        className={cs('flex h-full flex-col items-center justify-center text-center', className)}>
        {showIllustration && <Icon icon="illustration-permissions" />}
        <p
          className={cs(
            '!px-4 !pb-1 text-center text-base font-600 text-primary-900',
            messageClassName
          )}>
          {customMessage}
        </p>
        <p className="!px-4 text-center text-sm leading-6 text-neutral-600">{description}</p>
      </div>
    );
  };

  const hasRequiredModules =
    requiredModules && !(Array.isArray(requiredModules) && requiredModules.length === 0);

  if (!hasRequiredModules && displaySettings && !get(display_settings, displaySettings)) {
    if (showMessage) {
      return renderMessage();
    }
    return null;
  }

  if (hasRequiredModules) {
    const hasRequiredModule = isAnyModule
      ? checkModule(practiceModules, requiredModules, 'or')
      : checkModule(practiceModules, requiredModules);
    if (!hasRequiredModule) {
      if (showMessage) {
        return renderMessage();
      }
      return null;
    }
  }

  if (!requiredPermissions) {
    return children;
  }

  if (isAny && pm(permissions, requiredPermissions, 'or')) {
    return children;
  }

  if (pm(permissions, requiredPermissions)) {
    return children;
  }

  if (showMessage) {
    return renderMessage();
  }

  return null;
};

export default withErrorBoundary(PermissionsAllowed);
